/**
 * Project-level Google Health controls: the daily fetch (on/off, study dates), a fetch-now for
 * every connected participant, and the project's fetch history (manual and daily jobs).
 * Backend: health/ functions setHealthDailyFetch, startHealthFetch, dailyHealthFetch.
 */
import {
  collection, getDocs, limit, onSnapshot, orderBy, query, where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import { useAlert } from '../../context/AlertContext';
import { db, functions } from '../../firebase';
import cancelFetch from './cancelFetch';

const DAY = 86400000;
const isoDay = (ms) => new Date(ms).toISOString().slice(0, 10);
const todayMinus = (n) => isoDay(Date.now() - n * DAY);
const HISTORY_LIMIT = 30;

const STATUS = {
  queued: ['queued', 'ja-chip-enabled'],
  running: ['running', 'ja-chip-enabled'],
  done: ['done', 'ja-chip-active'],
  done_with_errors: ['done with errors', 'ja-chip-partial'],
  failed: ['failed', 'ja-chip-partial'],
  cancelled: ['cancelled', 'ja-chip-inactive'],
};

/** What the next daily run fetches; same rule as health/src/schedule.js (display only). */
function previewWindow(s) {
  if (!s?.enabled) return null;
  const today = Date.parse(isoDay(Date.now()));
  const lookback = Number(s.lookbackDays) || 3;
  let start = isoDay(today - lookback * DAY);
  let end = isoDay(today - DAY);
  if (s.startDate && s.startDate > start) start = s.startDate;
  if (s.endDate && s.endDate < end) end = s.endDate;
  return start <= end ? { start, end } : null;
}

const fmtTime = (ts) => (ts?.toDate ? ts.toDate().toLocaleString() : '…');

const th = {
  textAlign: 'left', fontWeight: 500, fontSize: 12, color: 'var(--jp-gray-600)',
  padding: '6px 10px', borderBottom: '1px solid var(--jp-gray-200)', whiteSpace: 'nowrap',
};
const td = { padding: '7px 10px', borderBottom: '1px solid var(--jp-gray-100)', fontSize: 13, verticalAlign: 'top' };
const muted = { fontSize: 13, color: 'var(--jp-gray-600)', margin: '0 0 12px' };

function Chip({ status }) {
  const [label, cls] = STATUS[status] || [status || '?', 'ja-chip-inactive'];
  return <span className={`ja-chip ${cls}`}>{label}</span>;
}
Chip.propTypes = { status: PropTypes.string };

/** Per-participant outcomes of one job, loaded when its row is opened. */
function JobTasks({ jobId }) {
  const [tasks, setTasks] = useState(null);
  useEffect(() => {
    let live = true;
    getDocs(collection(db, 'healthFetchJobs', jobId, 'tasks'))
      .then((snap) => { if (live) setTasks(snap.docs.map((d) => d.data())); })
      .catch(() => { if (live) setTasks([]); });
    return () => { live = false; };
  }, [jobId]);

  if (!tasks) return <p style={muted}>Loading…</p>;
  if (!tasks.length) return <p style={muted}>No participant tasks in this job.</p>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={th}>Participant</th><th style={th}>From</th><th style={th}>Status</th>
          <th style={th}>Rows</th><th style={th}>Notes</th>
        </tr>
      </thead>
      <tbody>
        {tasks.map((t) => {
          const typeErrors = Object.entries(t.perType || {}).filter(([, v]) => v?.error).map(([k]) => k);
          return (
            <tr key={`${t.participantId}_${t.startDate}`}>
              <td style={td}><code>{t.participantId}</code></td>
              <td style={td}>{t.startDate}</td>
              <td style={td}><Chip status={t.status} /></td>
              <td style={td}>{t.rows ?? 0}</td>
              <td style={{ ...td, color: 'var(--jp-gray-600)' }}>
                {t.error && <div>{t.error === 'NOT_ACTIVE' || t.error === 'invalid_grant'
                  ? 'Google access expired or revoked: participant must reconnect' : t.error}</div>}
                {typeErrors.length > 0 && <div>Not available: {typeErrors.join(', ')}</div>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
JobTasks.propTypes = { jobId: PropTypes.string.isRequired };

export default function ProjectHealthPanel({ project, projectId }) {
  const { addAlert } = useAlert();
  const saved = project?.healthDailyFetch || null;
  const savedKey = JSON.stringify(saved || {});

  // dataTypes: null means every type (also future ones); otherwise the chosen type IDs.
  const [form, setForm] = useState({ enabled: false, startDate: '', endDate: '', lookbackDays: 3, dataTypes: null });
  const [catalogue, setCatalogue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [connected, setConnected] = useState(null);
  const [range, setRange] = useState({ startDate: todayMinus(7), endDate: todayMinus(1) });
  const [fetching, setFetching] = useState(false);
  const [jobs, setJobs] = useState(null);
  const [jobsError, setJobsError] = useState(null);
  const [openJob, setOpenJob] = useState(null);

  // Load the saved settings into the form whenever they change in Firestore.
  useEffect(() => {
    const s = JSON.parse(savedKey);
    setForm({
      enabled: s.enabled === true,
      startDate: s.startDate || '',
      endDate: s.endDate || '',
      lookbackDays: s.lookbackDays || 3,
      dataTypes: Array.isArray(s.dataTypes) && s.dataTypes.length ? s.dataTypes : null,
    });
  }, [savedKey]);

  // The data types Jamasp can fetch (from the backend, so new types appear here automatically).
  useEffect(() => {
    let live = true;
    httpsCallable(functions, 'listHealthDataTypes')()
      .then((r) => { if (live) setCatalogue(r.data?.dataTypes || []); })
      .catch(() => { if (live) setCatalogue([]); });
    return () => { live = false; };
  }, []);

  // How many participants the next fetch would cover.
  useEffect(() => {
    let live = true;
    getDocs(query(collection(db, 'participants'),
      where('projectId', '==', projectId), where('health.connected', '==', true)))
      .then((snap) => { if (live) setConnected(snap.size); })
      .catch(() => { if (live) setConnected(null); });
    return () => { live = false; };
  }, [projectId]);

  // Fetch history, newest first, updating live while jobs run.
  useEffect(() => onSnapshot(
    query(collection(db, 'healthFetchJobs'), where('projectId', '==', projectId),
      orderBy('createdAt', 'desc'), limit(HISTORY_LIMIT)),
    (snap) => {
      setJobsError(null);
      setJobs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (err) => {
      console.error('healthFetchJobs query failed', err);
      setJobsError(err.code === 'failed-precondition'
        ? 'History is unavailable until its Firestore index is deployed and finished building (a few minutes).'
        : `History could not be loaded: ${err.message}`);
      setJobs([]);
    },
  ), [projectId]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await httpsCallable(functions, 'setHealthDailyFetch')({ projectId, ...form });
      const w = res.data?.nextWindow;
      addAlert('success', form.enabled
        ? (w ? `Daily fetch on. Next run fetches ${w.startDate} to ${w.endDate}.`
          : 'Daily fetch on, but today is outside the study dates, so nothing will be fetched yet.')
        : 'Daily fetch off.');
    } catch (err) {
      addAlert('error', `Could not save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const fetchNow = async () => {
    setFetching(true);
    try {
      const res = await httpsCallable(functions, 'startHealthFetch')({
        projectId, ...range, ...(form.dataTypes ? { dataTypes: form.dataTypes } : {}),
      });
      addAlert('success', res.data.tasks
        ? `Fetch queued (${res.data.tasks} task${res.data.tasks === 1 ? '' : 's'}). Progress shows below.`
        : 'No connected participants, so there was nothing to fetch.');
    } catch (err) {
      addAlert('error', `Could not start fetch: ${err.message}`);
    } finally {
      setFetching(false);
    }
  };

  const next = previewWindow(saved);
  const allIds = (catalogue || []).map((t) => t.id);
  const selected = new Set(form.dataTypes || allIds);
  const noneSelected = catalogue?.length > 0 && selected.size === 0;
  const selectionChanged = JSON.stringify(form.dataTypes || null) !==
    JSON.stringify((Array.isArray(saved?.dataTypes) && saved.dataTypes.length) ? saved.dataTypes : null);
  const setSelection = (ids) => {
    const keep = allIds.filter((id) => ids.has(id)); // catalogue order
    setForm({ ...form, dataTypes: keep.length === allIds.length ? null : keep });
  };
  const toggleType = (id) => {
    const nextSet = new Set(selected);
    if (nextSet.has(id)) nextSet.delete(id); else nextSet.add(id);
    setSelection(nextSet);
  };
  const groups = [];
  for (const t of catalogue || []) {
    let g = groups.find((x) => x.id === t.group);
    if (!g) groups.push(g = { id: t.group, label: t.groupLabel, types: [] });
    g.types.push(t);
  }
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="ja-card">
        <div className="ja-card-header">
          <h3 className="ja-card-title">Daily fetch</h3>
          <span className={`ja-chip ${saved?.enabled ? 'ja-chip-active' : 'ja-chip-inactive'}`}>
            {saved?.enabled ? 'on' : 'off'}
          </span>
        </div>
        <p style={muted}>
          Every day at 06:00 New York time, Jamasp fetches the most recent days for every connected
          participant in this project{connected != null ? ` (currently ${connected})` : ''} into BigQuery.
          Recent days are fetched again on later runs to catch late syncs. Collection stops by itself
          after the end date.
          {saved?.enabled && (next
            ? <> Next run: <strong>{next.start} to {next.end}</strong>.</>
            : <> Today is outside the study dates, so nothing is being fetched.</>)}
        </p>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 8 }}>
            <input type="checkbox" checked={form.enabled} onChange={set('enabled')} /> Fetch every day
          </label>
          <label style={{ fontSize: 13 }}>Study start (optional)<br />
            <input type="date" className="ja-input" value={form.startDate} onChange={set('startDate')} />
          </label>
          <label style={{ fontSize: 13 }}>Study end (optional)<br />
            <input type="date" className="ja-input" value={form.endDate} onChange={set('endDate')} />
          </label>
          <label style={{ fontSize: 13 }}>Days per run<br />
            <input type="number" min={1} max={14} className="ja-input" style={{ width: 80 }}
              value={form.lookbackDays} onChange={set('lookbackDays')} />
          </label>
          <button className="ja-btn ja-btn-primary" disabled={saving || noneSelected} onClick={saveSettings}>Save</button>
        </div>
      </div>

      <div className="ja-card">
        <div className="ja-card-header">
          <h3 className="ja-card-title">Data types</h3>
          <span className="ja-card-meta">
            {catalogue ? `${selected.size} of ${allIds.length} selected` : ''}
          </span>
        </div>
        <p style={muted}>
          What the daily fetch and Fetch now collect for this project. A type a participant&apos;s device
          doesn&apos;t record simply comes back empty. Changes apply to the daily fetch once saved; Fetch now
          uses the selection shown here.
        </p>
        {catalogue === null && <p style={muted}>Loading…</p>}
        {catalogue?.length === 0 && <p style={{ ...muted, color: '#C0392B' }}>Could not load the data types.</p>}
        {catalogue?.length > 0 && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16 }}>
              {groups.map((g) => (
                <div key={g.id}>
                  <span className="ja-label" style={{ marginBottom: 6 }}>{g.label}</span>
                  {g.types.map((t) => (
                    <label key={t.id} title={t.id}
                      style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, padding: '3px 0', cursor: 'pointer' }}>
                      <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleType(t.id)} />
                      {t.label}
                    </label>
                  ))}
                </div>
              ))}
            </div>
            <div className="ja-btn-row" style={{ marginTop: 14, alignItems: 'center' }}>
              <button className="ja-btn ja-btn-ghost" onClick={() => setSelection(new Set(allIds))}>Select all</button>
              <button className="ja-btn ja-btn-ghost" onClick={() => setSelection(new Set())}>Clear</button>
              <button className="ja-btn ja-btn-primary" disabled={saving || noneSelected || !selectionChanged}
                onClick={saveSettings}>
                Save selection
              </button>
              {noneSelected && <span style={{ fontSize: 13, color: '#C0392B' }}>Select at least one type.</span>}
            </div>
          </>
        )}
      </div>

      <div className="ja-card">
        <div className="ja-card-header">
          <h3 className="ja-card-title">Fetch now</h3>
        </div>
        <p style={muted}>
          Fetch a date range for every connected participant in this project. Data already in BigQuery
          for those days is updated, not duplicated.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>From<br />
            <input type="date" className="ja-input" value={range.startDate}
              onChange={(e) => setRange({ ...range, startDate: e.target.value })} />
          </label>
          <label style={{ fontSize: 13 }}>To (inclusive)<br />
            <input type="date" className="ja-input" value={range.endDate}
              onChange={(e) => setRange({ ...range, endDate: e.target.value })} />
          </label>
          <button className="ja-btn ja-btn-primary"
            disabled={fetching || noneSelected || !range.startDate || !range.endDate}
            onClick={fetchNow}>
            Fetch into BigQuery
          </button>
        </div>
      </div>

      <div className="ja-card">
        <div className="ja-card-header">
          <h3 className="ja-card-title">Fetch history</h3>
          <span className="ja-card-meta">last {HISTORY_LIMIT}</span>
        </div>
        {jobs === null && <p style={muted}>Loading…</p>}
        {jobsError && <p style={{ ...muted, color: '#C0392B' }}>{jobsError}</p>}
        {!jobsError && jobs?.length === 0 && <p style={muted}>No Google Health fetches for this project yet.</p>}
        {jobs?.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Started</th><th style={th}>Type</th><th style={th}>Dates</th>
                  <th style={th}>Status</th><th style={th}>Participants</th><th style={th}>Rows</th>
                  <th style={th} aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <Fragment key={j.id}>
                    <tr onClick={() => setOpenJob(openJob === j.id ? null : j.id)}
                      style={{ cursor: 'pointer', background: openJob === j.id ? 'var(--jp-gray-100)' : undefined }}>
                      <td style={td}>{fmtTime(j.createdAt)}</td>
                      <td style={td}>{j.type || 'manual'}</td>
                      <td style={td}>{j.startDate}{j.endDate !== j.startDate ? ` to ${j.endDate}` : ''}</td>
                      <td style={td}><Chip status={j.status} /></td>
                      <td style={td}>
                        {j.tasksDone || 0}/{j.tasksTotal || 0} tasks
                        {j.tasksFailed ? `, ${j.tasksFailed} failed` : ''}
                        {j.tasksCancelled ? `, ${j.tasksCancelled} skipped` : ''}
                        {j.skippedNotConnected?.length ? ` · ${j.skippedNotConnected.length} not connected` : ''}
                      </td>
                      <td style={td}>{(j.rowsWritten || 0).toLocaleString()}</td>
                      <td style={td}>
                        {(j.status === 'queued' || j.status === 'running') && (
                          <button type="button" className="ja-btn ja-btn-ghost"
                            onClick={(e) => { e.stopPropagation(); cancelFetch(j.id, addAlert); }}>
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                    {openJob === j.id && (
                      <tr>
                        <td colSpan={7} style={{ ...td, background: 'var(--jp-gray-100)' }}>
                          <JobTasks jobId={j.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

ProjectHealthPanel.propTypes = {
  project: PropTypes.shape({
    healthDailyFetch: PropTypes.shape({
      enabled: PropTypes.bool,
      dataTypes: PropTypes.arrayOf(PropTypes.string),
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      lookbackDays: PropTypes.number,
    }),
  }),
  projectId: PropTypes.string.isRequired,
};
